import { DocumentNode } from 'graphql';
import * as React from 'react';
import { SetRequired } from 'type-fest';

import { FullScreenModalProps } from '@/components/modals/FullScreenModal/FullScreenModal';
import { AIOnboardingModalFormShapeKeys } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModalForm/AIOnboardingModalForm.types';
import {
  AiOnboardingModal_AiSuggestionFormDataFragment,
  AiOnboardingModal_AiSuggestionFragment,
  AiOnboardingModal_HouseholdFragment,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { DocumentViewerTab } from '@/modules/documents/components/HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.context';
import { DocumentSearchResult } from '@/modules/pdf/DocumentSearch/DocumentSearch.types';
import { RouteKey } from '@/navigation/constants';
import { AiSuggestionKind, AiSuggestionWhereInput } from '@/types/schema';

export interface OpenAIOnboardingModalProps {
  householdId: string;
  variant: AIOnboardingModalVariant;
  initialSelectedSuggestion?: AiSuggestionForReviewCard;
}

export type AIOnboardingModalProps = OpenAIOnboardingModalProps &
  SetRequired<
    Pick<FullScreenModalProps, 'isOpen' | 'onClose'>,
    'isOpen' | 'onClose'
  > & {
    suggestionsWhere: AiSuggestionWhereInput;
    /**
     * Optional additional suggestions that should be available for reference
     * (e.g., in suggestionsByID) but not shown as suggestions to review.
     */
    additionalReferenceSuggestions?: AiOnboardingModal_AiSuggestionFragment[];
    /**
     * Optional flag to skip navigation after completing the modal.
     * When true, the modal will simply close without navigating to the
     * onCompleteNavigationRoute. Useful for Estate page components where
     * users should remain on their current tab.
     */
    skipNavigationOnComplete?: boolean;
  };

export type AIOnboardingModalProviderProps = SetRequired<
  Pick<FullScreenModalProps, 'isOpen' | 'onClose'>,
  'isOpen' | 'onClose'
> & {
  loading: boolean;
  variant: AIOnboardingModalVariant;
  household: AiOnboardingModal_HouseholdFragment;
  /**
   * All the relevant suggestions in the household, regardless of acceptance status.
   */
  suggestions: AiOnboardingModal_AiSuggestionFragment[];
  /**
   * Every pending suggestion in the modal's query scope, across all pages.
   * Confirm/Confirm all submits this whole set — suggestions on pages the
   * user never visited are created with their suggested values — so a
   * paginated Review all is never a per-page submission.
   */
  suggestionsForSubmission: AiOnboardingModal_AiSuggestionFormDataFragment[];
  /**
   * Optional additional suggestions that should be available for reference
   * (e.g., in suggestionsByID) but not shown as suggestions to review.
   */
  additionalReferenceSuggestions?: AiOnboardingModal_AiSuggestionFragment[];
  footerRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Optional flag to skip navigation after completing the modal.
   */
  skipNavigationOnComplete?: boolean;
  suggestionsPagination: AIOnboardingModalSuggestionsPagination;
  onComplete: () => void;
  onSuggestionSaved: (
    suggestionID: string
  ) => AIOnboardingModalSuggestionSavedResult;
  reviewedSuggestionIDs: string[];
};

export interface AIOnboardingModalSuggestionSavedResult {
  isFinalSuggestion: boolean;
}

export interface AIOnboardingModalSuggestionsPagination {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  currentPageCount: number;
  reviewedCountBeforeCurrentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

export type AIOnboardingModalSuggestionKinds =
  | AiSuggestionKind.OnboardingIndividual
  | AiSuggestionKind.OnboardingOrganization
  | AiSuggestionKind.OnboardingEntity
  | AiSuggestionKind.OnboardingTestamentaryEntity
  | AiSuggestionKind.Asset
  | AiSuggestionKind.Liability
  | AiSuggestionKind.LifeInsurance;

export enum AIOnboardingModalVariant {
  ALL_ENTITIES = 'ALL_ENTITIES', // This is used for household entities BEFORE the balance-sheet/estate pages
  TRUST_ENTITIES = 'TRUST_ENTITIES', // This is used for the TRUST entities on the estate pages
  BUSINESS_ENTITIES = 'BUSINESS_ENTITIES', // This is used for the BUSINESS entities on the estate pages
  HOLDINGS = 'HOLDINGS',
}

export interface AIOnboardingModalVariantConfig {
  variant: AIOnboardingModalVariant;
  getHeaderText: (count: number) => string;
  suggestionKinds: AIOnboardingModalSuggestionKinds[];
  formPaths: AIOnboardingModalFormShapeKeys[];
  onCompleteNavigationRoute: RouteKey;
  refetchQueries: DocumentNode[] | undefined;
}

export interface AIOnboardingDocumentPanelProps {
  activeDocumentViewerTab: DocumentViewerTab | undefined;
  activeDocumentId: string | undefined;
  searchQuery: string;
  annotations: DocumentSearchResult[] | undefined;
  activeAnnotationIndex: number | undefined;
  searchLoading: boolean;
}
